import { useRef, useState } from 'react';
import { FileText, ShieldCheck } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { useLanguage } from '../lib/site';
import { privacyPolicy } from '../content/privacyPolicy';
import { termsConditions } from '../content/termsConditions';
import './LegalDocuments.css';

const documents = { privacy: privacyPolicy, terms: termsConditions };

const LegalText = ({ document, documentId }) => document.text.split('\n\n').map((block, index) => {
  const testId = `legal-${documentId}-block-${index}`;
  if (block.startsWith('## ')) {
    const title = block.slice(3);
    const number = title.split('.')[0];
    return <h3 key={index} data-testid={`legal-${documentId}-section-${number}`}>{title}</h3>;
  }
  if (block.startsWith('### ')) return <h4 key={index} data-testid={testId}>{block.slice(4)}</h4>;
  if (block.startsWith('- ')) return <ul key={index} data-testid={testId}>{block.split('\n').map((line, itemIndex) => <li key={itemIndex} data-testid={`${testId}-item-${itemIndex}`}>{line.slice(2)}</li>)}</ul>;
  return <p key={index} data-testid={testId}>{block}</p>;
});

export const LegalDocuments = () => {
  const { t, lang } = useLanguage();
  const [active, setActive] = useState('privacy');
  const [open, setOpen] = useState(false);
  const opener = useRef(null);
  const heading = useRef(null);
  const document = documents[active];
  const showDocument = (event, id) => {
    opener.current = event.currentTarget;
    setActive(id);
    setOpen(true);
  };
  const updatedDate = new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${document.updatedAt}T12:00:00Z`));

  return <>
    <div className="footer-legal" data-testid="footer-legal-buttons" role="group" aria-label={t('Documente juridice', 'Legal documents')}>
      <button type="button" className="footer-legal-button" data-testid="footer-privacy-button" aria-haspopup="dialog" onClick={event => showDocument(event, 'privacy')}><ShieldCheck size={17}/>{t('Politica de confidențialitate', 'Privacy policy')}</button>
      <button type="button" className="footer-legal-button" data-testid="footer-terms-button" aria-haspopup="dialog" onClick={event => showDocument(event, 'terms')}><FileText size={17}/>{t('Termeni și condiții', 'Terms and conditions')}</button>
    </div>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="legal-dialog" data-testid="legal-document-dialog" lang="ro" data-lenis-prevent onOpenAutoFocus={event => { event.preventDefault(); heading.current?.focus(); }} onCloseAutoFocus={event => { event.preventDefault(); opener.current?.focus({ preventScroll: true }); }}>
        <div className="legal-dialog-header">
          <span className="eyebrow" data-testid="legal-brand">ORANJINA BISCOTINA</span>
          <DialogTitle ref={heading} tabIndex={-1} data-testid="legal-document-title">{document.title}</DialogTitle>
          <DialogDescription className="sr-only" data-testid="legal-document-description">Documentul poate fi citit integral prin derulare. Apasă Escape sau butonul Închide pentru a reveni la pagină.</DialogDescription>
          <p className="legal-updated" data-testid="legal-updated-date">Ultima actualizare: <time dateTime={document.updatedAt}>{updatedDate}</time></p>
          {lang === 'en' && <p className="legal-language-note" lang="en" data-testid="legal-language-note">This document is available in Romanian.</p>}
        </div>
        <div key={active} className="legal-document-body" data-testid="legal-document-body" tabIndex={0} role="region" aria-label={document.title} data-lenis-prevent>
          <LegalText document={document} documentId={active}/>
        </div>
        <div className="legal-dialog-footer"><DialogClose asChild><button type="button" className="button button-dark" data-testid="legal-document-done" lang={lang}>{t('Închide documentul', 'Close document')}</button></DialogClose></div>
      </DialogContent>
    </Dialog>
  </>;
};